import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { isNativeApp } from '@/lib/platform';
import { PLAN_TO_PRODUCT_ID, type ParentPlan } from '@/lib/parent/plan-seats';
import { markCheckoutStarted } from '@/lib/analytics/track';

// Les RPC `get_children_access` / `assign_child_seat` / `unassign_child_seat`
// (migration 0195) sont postérieures aux types générés (stale volontaire, cf.
// CLAUDE.md). Client générique, comme lib/parent-link.ts.
const db = supabase as unknown as SupabaseClient;

// ─── Types ────────────────────────────────────────────────────────────────

export type ChildAccessSource = 'subscription' | 'pass' | 'none';

export type ChildAccess = {
  child_id: string;
  display_name: string | null;
  avatar_url: string | null;
  has_access: boolean;
  source: ChildAccessSource;
  plan: string | null;
  seat_subscription_id: string | null;
  expires_at: string | null;
};

export type SeatOccupant = {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
};

export type ParentSubscription = {
  id: string;
  plan: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  seats: SeatOccupant[];
};

// ─── Invalidation ───────────────────────────────────────────────────────────

function invalidateParentSubs(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['children-access'] });
  qc.invalidateQueries({ queryKey: ['parent-subscriptions'] });
  qc.invalidateQueries({ queryKey: ['parent-children'] });
}

// ─── Queries ──────────────────────────────────────────────────────────────

/** Statut d'accès (subscription/pass/none) de chaque enfant lié actif. */
export function useChildrenAccess(parentId: string) {
  return useQuery({
    queryKey: ['children-access', parentId],
    enabled: !!parentId,
    staleTime: 30_000,
    queryFn: async (): Promise<ChildAccess[]> => {
      const { data, error } = await db.rpc('get_children_access');
      if (error) throw new Error(error.message);
      return (data ?? []) as ChildAccess[];
    },
  });
}

/** Abonnements possédés par le parent (owner) + occupants des sièges. */
export function useMyParentSubscriptions(parentId: string) {
  return useQuery({
    queryKey: ['parent-subscriptions', parentId],
    enabled: !!parentId,
    staleTime: 30_000,
    queryFn: async (): Promise<ParentSubscription[]> => {
      const { data, error } = await db
        .from('subscriptions')
        .select(
          'id, plan, status, current_period_end, cancel_at_period_end, ' +
            'subscription_seats(user_id, profiles:profiles!subscription_seats_user_id_fkey(id, display_name:visible_name, avatar_url))',
        )
        .eq('owner_id', parentId)
        .in('status', ['active', 'trialing'])
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as Record<string, unknown>[]).map((row) => ({
        id: row.id as string,
        plan: row.plan as string,
        status: row.status as string,
        current_period_end: (row.current_period_end as string | null) ?? null,
        cancel_at_period_end: !!row.cancel_at_period_end,
        seats: ((row.subscription_seats as Record<string, unknown>[] | null) ?? []).map((s) => {
          const p = s.profiles as Record<string, unknown> | null;
          return {
            user_id: s.user_id as string,
            display_name: (p?.display_name as string | null) ?? null,
            avatar_url: (p?.avatar_url as string | null) ?? null,
          };
        }),
      }));
    },
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Attribue un enfant lié à un siège de l'abonnement. */
export function useAssignChildSeat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { subscriptionId: string; childId: string }) => {
      const { error } = await db.rpc('assign_child_seat', {
        p_subscription_id: args.subscriptionId,
        p_child_id: args.childId,
      });
      if (error) throw new Error(error.message);
    },
    onSettled: () => invalidateParentSubs(qc),
  });
}

/** Retire un enfant d'un siège de l'abonnement. */
export function useUnassignChildSeat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { subscriptionId: string; childId: string }) => {
      const { error } = await db.rpc('unassign_child_seat', {
        p_subscription_id: args.subscriptionId,
        p_child_id: args.childId,
      });
      if (error) throw new Error(error.message);
    },
    onSettled: () => invalidateParentSubs(qc),
  });
}

// ─── Checkout / gestion (natif = IAP RevenueCat · web = Stripe) ─────────────

export type SubscriptionCheckoutResult =
  | { platform: 'web' } // redirection Stripe (la nav emporte la page)
  | { platform: 'native'; status: 'success' | 'pending' };

/**
 * Souscrit un abonnement pour le parent.
 *   - **Natif (iOS/Android)** : achat In-App Purchase via RevenueCat (obligation
 *     Apple 3.1.1). Le webhook RC crée la sub (owner = parent) en async → on
 *     attend son apparition puis on rattache l'enfant sélectionné (best-effort ;
 *     sinon le parent l'attribue à la main).
 *   - **Web** : Stripe Checkout (redirection). Le webhook attribue le siège via
 *     `target_child_id`.
 */
export function useSubscriptionCheckout(parentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      plan: ParentPlan;
      childId?: string | null;
    }): Promise<SubscriptionCheckoutResult> => {
      if (isNativeApp()) {
        const { purchase } = await import('@/lib/checkout');
        const res = await purchase(PLAN_TO_PRODUCT_ID[args.plan] as never);
        if (res.status === 'user_cancelled') throw new Error('user_cancelled');
        if (res.status !== 'success' && res.status !== 'pending') {
          throw new Error(res.message ?? 'purchase_failed');
        }
        const subId = await waitForNativeSubscription(parentId, args.plan);
        if (subId && args.childId) {
          try {
            await db.rpc('assign_child_seat', {
              p_subscription_id: subId,
              p_child_id: args.childId,
            });
          } catch {
            /* attribution manuelle possible ensuite */
          }
        }
        invalidateParentSubs(qc);
        return { platform: 'native', status: res.status };
      }

      markCheckoutStarted(
        args.plan === 'family_monthly' ? 'abonnement-famille' : 'abonnement-eleve',
      );
      // Web → Stripe checkout (redirection). Erreurs métier (502
      // price_not_provisioned, 400) → slug remonté depuis le corps.
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: {
          plan: args.plan,
          target_child_id: args.childId ?? undefined,
          return_to: window.location.href,
        },
      });
      if (error || !data?.url) {
        const slug = await extractInvokeErrorSlug(error);
        throw new Error(slug ?? 'checkout_failed');
      }
      window.location.href = data.url as string;
      return { platform: 'web' };
    },
  });
}

/**
 * Attend l'apparition de la sub IAP fraîchement achetée (webhook RC async).
 * ~12 s max ; renvoie son id ou null (le parent pourra attribuer plus tard).
 */
async function waitForNativeSubscription(parentId: string, plan: string): Promise<string | null> {
  for (let i = 0; i < 6; i++) {
    const { data } = await db
      .from('subscriptions')
      .select('id')
      .eq('owner_id', parentId)
      .eq('plan', plan)
      .in('status', ['active', 'trialing'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const id = (data as { id?: string } | null)?.id;
    if (id) return id;
    await new Promise((r) => setTimeout(r, 2000));
  }
  return null;
}

/**
 * Gérer / changer de plan / résilier.
 *   - Natif : renvoie vers les Réglages du store (Apple/Google) — un abonnement
 *     IAP ne se gère PAS via le portail Stripe.
 *   - Web : portail client Stripe.
 */
export function useManageSubscription() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      if (isNativeApp()) {
        const { openManageSubscriptionsNative } = await import('@/lib/checkout/native');
        await openManageSubscriptionsNative();
        return;
      }
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { return_to: window.location.href },
      });
      if (error || !data?.url) throw new Error('portal_failed');
      window.location.href = data.url as string;
    },
  });
}

/** Extrait le slug d'erreur métier d'une FunctionsHttpError (corps JSON). */
async function extractInvokeErrorSlug(error: unknown): Promise<string | null> {
  const ctx = (error as { context?: Response } | null)?.context;
  if (!ctx || typeof ctx.json !== 'function') return null;
  try {
    const body = (await ctx.json()) as { error?: string };
    return body?.error ?? null;
  } catch {
    return null;
  }
}
